import { Box, Stack } from "@mui/material";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { useState } from "react";

import { getProjectStateOptions } from "Forms/Utils/EnumOptions";
import type { ProjectState } from "Infrastructure/Api/Api";
import useDebounce from "Infrastructure/Hooks/useDebounce";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useProjectsListQuery } from "Projects/Api/Queries/useProjectsListQuery";
import { ClientFilterSelect } from "Projects/Components/ClientFilterSelect";
import { TableSearchInput } from "Projects/Components/TableSearchInput";
import { useProjectsTableColumns } from "Projects/Components/useProjectsTableColumns";
import { BlTanstackTable } from "Shared/DataTable/BlTanstackTable";
import { BlDropdown } from "Shared/Inputs/BlDropdown";
import type { BlOption } from "Shared/Inputs/BlOption";

export const RecentProjectsTable = (): React.ReactNode => {
  const { t } = useResource();

  const [searchInputValue, setSearchInputValue] = useState("");
  const debouncedSearchQuery = useDebounce(searchInputValue, 300);

  const [selectedClient, setSelectedClient] = useState<BlOption | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<ProjectState | "">("");

  const { data, isLoading } = useProjectsListQuery({
    offset: 0,
    limit: 5,
    clientID: selectedClient?.value?.toString() || null,
    searchQuery: debouncedSearchQuery || null,
    projectState: selectedStatus || null,
  });

  const projects = data?.items ?? [];

  const statusOptions: BlOption[] = [
    { value: "", label: t(Resources.Dashboard.AllStatuses) },
    ...getProjectStateOptions(),
  ];

  const columns = useProjectsTableColumns();

  const table = useReactTable({
    data: projects,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <>
      <Stack
        direction="row"
        spacing={Spacing.S}
        sx={{ marginBottom: Spacing.M }}
      >
        <Box sx={{ flex: 1 }}>
          <TableSearchInput
            name="project-search"
            value={searchInputValue}
            onChange={setSearchInputValue}
            placeholder={t(Resources.Dashboard.SearchProjectsPlaceholder)}
          />
        </Box>
        <Box sx={{ minWidth: 200 }}>
          <ClientFilterSelect
            value={selectedClient}
            onChange={(value) => setSelectedClient(value)}
          />
        </Box>
        <Box sx={{ minWidth: 200 }}>
          <BlDropdown
            name="status-filter"
            options={statusOptions}
            value={selectedStatus}
            onChange={(value) => setSelectedStatus(value as ProjectState | "")}
            placeholder={t(Resources.Dashboard.Status)}
          />
        </Box>
      </Stack>

      <BlTanstackTable
        table={table}
        isLoading={isLoading}
        isPaginationDisabled
      />
    </>
  );
};
